import { ChangeDetectionStrategy, Component, forwardRef, input, signal, effect, ChangeDetectorRef, inject, model } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ScrollIntoViewDirective } from '../scroll-into-view/scroll-into-view.directive';

@Component({
  selector: 'zag-input',
  imports: [ScrollIntoViewDirective],
  templateUrl: './input.html',
  styleUrl: './input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Input),
      multi: true,
    },
  ],
})
export class Input implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);

  // Input properties with signals
  readonly type = input<string>('text');
  readonly placeholder = input<string>('');
  readonly disabled = input<boolean>(false);
  readonly readonly = input<boolean>(false);
  readonly id = input<string | undefined>(undefined);
  readonly name = input<string | undefined>(undefined);
  readonly autocomplete = input<string | undefined>(undefined);
  readonly accept = input<string | undefined>(undefined);
  readonly pattern = input<string | undefined>(undefined);
  readonly required = input<boolean>(false);
  readonly inputClass = input<string>('');
  readonly multiline = input<boolean>(false);
  readonly rows = input<number>(4);
  readonly value = model<string>('');
  readonly ariaDescribedby = input<string | undefined>(undefined);

  // Internal state
  protected readonly focused = signal<boolean>(false);

  // ControlValueAccessor callbacks
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  constructor() {
    // Sync disabled state effect
    effect(() => {
      if (this.disabled()) {
        this.cdr.markForCheck();
      }
    });
  }

  writeValue(value: string): void {
    this.value.set(value ?? '');
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    this.cdr.markForCheck();
  }

  protected onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (this.type() === 'file') {
      return;
    }

    const newValue = target.value;
    this.value.set(newValue);
    this.onChange(newValue);
  }

  protected onBlur(): void {
    this.focused.set(false);
    this.onTouched();
  }

  protected onFocus(): void {
    this.focused.set(true);
  }
}
